import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, EyeOff, Leaf, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { SEOMeta } from '@/components/SEOMeta';

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) { navigate(from, { replace: true }); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Грешни имейл или парола');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOMeta
        title="Вход"
        description="Влез в акаунта си в portion.bg и получи достъп до персонализирания си хранителен план."
        canonical="/login"
        noindex={true}
      />
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <motion.div className="w-full max-w-sm" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center mb-7">
            <img
              src="https://customer-assets.emergentagent.com/job_budget-meals-37/artifacts/4w0xeexu_Screenshot%202026-06-21%20at%2018.49.11-Photoroom.png"
              alt="Portion Budget Meals"
              className="h-44 w-auto object-contain drop-shadow-lg dark:brightness-0 dark:invert"
            />
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">Добре дошли</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Влезте в акаунта си</p>
        </div>
        <Card className="card-shadow rounded-xl">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-gray-900 dark:text-gray-200 text-sm font-medium">Имейл</Label>
                <Input id="email" type="email" placeholder="you@example.com" value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  className="mt-1.5 h-11 border-[#E6EDF5] dark:border-slate-600 focus:border-emerald-500 dark:focus:border-emerald-400 dark:bg-slate-800 dark:text-gray-200" required />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-900 dark:text-gray-200 text-sm font-medium">Парола</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPw ? 'text' : 'password'} placeholder="мин. 8 символа" value={form.password}
                    onChange={e => setForm(f => ({...f, password: e.target.value}))}
                    className="h-11 pr-10 border-[#E6EDF5] dark:border-slate-600 focus:border-emerald-500 dark:focus:border-emerald-400 dark:bg-slate-800 dark:text-gray-200" required />
                  <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white min-h-11" data-testid="login-form-submit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Вход'}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
              Нямаш акаунт?{' '}
              <Link to="/register" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium">Регистрирай се</Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
    </>
  );
}
